'use client';

import React from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { ParticipantEvent } from 'livekit-client';

interface HostSettingsContextValue {
  isHost: boolean;
  allowGuestsToRecord: boolean;
  setAllowGuestsToRecord: (
    allow: boolean,
    options?: {
      propagate?: boolean;
    },
  ) => void;
  lastUpdatedBy?: string | null;
}

const HostSettingsContext = React.createContext<HostSettingsContextValue | undefined>(undefined);

const DEFAULT_ALLOW_GUESTS_TO_RECORD = false;

type HostSettingsProviderProps = {
  children: React.ReactNode;
  forceHost?: boolean;
};

export function HostSettingsProvider({ children, forceHost = false }: HostSettingsProviderProps) {
  const room = useRoomContext();
  const localParticipant = useLocalParticipant();

  const [allowGuestsToRecord, setAllowGuestsToRecordState] = React.useState<boolean>(
    DEFAULT_ALLOW_GUESTS_TO_RECORD,
  );
  const [lastUpdatedBy, setLastUpdatedBy] = React.useState<string | null>(null);
  const [localMetadata, setLocalMetadata] = React.useState<string | null>(null);

  const allowGuestsRef = React.useRef(allowGuestsToRecord);
  React.useEffect(() => {
    allowGuestsRef.current = allowGuestsToRecord;
  }, [allowGuestsToRecord]);

  React.useEffect(() => {
    if (!room) return;

    const participant = room.localParticipant;
    const handleMetadataChanged = () => {
      setLocalMetadata(participant.metadata ?? null);
    };

    handleMetadataChanged();
    participant.on(ParticipantEvent.ParticipantMetadataChanged, handleMetadataChanged);

    return () => {
      participant.off(ParticipantEvent.ParticipantMetadataChanged, handleMetadataChanged);
    };
  }, [room]);

  const isHost = React.useMemo(() => {
    if (forceHost) {
      return true;
    }
    const metadata = localMetadata ?? localParticipant.localParticipant?.metadata;
    if (!metadata) return false;
    try {
      const parsed = JSON.parse(metadata);
      return parsed.isHost === true || parsed.role === 'host' || parsed.forceHost === true;
    } catch (error) {
      return false;
    }
  }, [forceHost, localMetadata, localParticipant.localParticipant?.metadata]);

  const broadcastRecordingPermission = React.useCallback(
    async (allow: boolean) => {
      if (!room) return;
      if (room.state !== 'connected') {
        return;
      }
      const localParticipant = room.localParticipant;
      if (!localParticipant) {
        return;
      }
      try {
        const payload = {
          type: 'host-settings',
          action: 'recording-permissions',
          allowGuestsToRecord: allow,
          timestamp: Date.now(),
          from: localParticipant.identity ?? null,
        };
        await localParticipant.publishData(new TextEncoder().encode(JSON.stringify(payload)), {
          reliable: true,
        });
      } catch (error) {
        console.error('Erro ao enviar configuração de gravação do anfitrião:', error);
      }
    },
    [room],
  );

  const setAllowGuestsToRecord = React.useCallback<
    HostSettingsContextValue['setAllowGuestsToRecord']
  >(
    (allow, options) => {
      setAllowGuestsToRecordState(allow);
      setLastUpdatedBy(room?.localParticipant?.identity ?? null);

      if (options?.propagate && (isHost || forceHost)) {
        broadcastRecordingPermission(allow);
      }
    },
    [broadcastRecordingPermission, forceHost, isHost, room?.localParticipant?.identity],
  );

  // Receber atualizações do anfitrião
  React.useEffect(() => {
    if (!room) return;

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: { identity?: string | null },
    ) => {
      try {
        const decoded = new TextDecoder().decode(payload);
        const data = JSON.parse(decoded);

        if (data?.type !== 'host-settings') return;
        if (data.action !== 'recording-permissions') return;

        if (typeof data.allowGuestsToRecord === 'boolean') {
          setAllowGuestsToRecordState(data.allowGuestsToRecord);
          setLastUpdatedBy(
            (data.from as string | null | undefined) ?? participant?.identity ?? null,
          );
        }
      } catch (error) {
        console.error('Erro ao processar dados de configuração do anfitrião:', error);
      }
    };

    room.on('dataReceived', handleDataReceived);

    return () => {
      room.off('dataReceived', handleDataReceived);
    };
  }, [room]);

  // Garantir que novos participantes recebam o estado atual quando o anfitrião estiver presente
  React.useEffect(() => {
    if (!room || !isHost) return;

    const emitStateToParticipant = () => {
      broadcastRecordingPermission(allowGuestsRef.current);
    };

    room.on('participantConnected', emitStateToParticipant);

    // Emite logo que possível para sincronizar participantes já conectados
    emitStateToParticipant();

    return () => {
      room.off('participantConnected', emitStateToParticipant);
    };
  }, [room, isHost, broadcastRecordingPermission]);

  const value = React.useMemo<HostSettingsContextValue>(
    () => ({
      isHost,
      allowGuestsToRecord,
      setAllowGuestsToRecord,
      lastUpdatedBy,
    }),
    [allowGuestsToRecord, isHost, lastUpdatedBy, setAllowGuestsToRecord],
  );

  return <HostSettingsContext.Provider value={value}>{children}</HostSettingsContext.Provider>;
}

export function useHostSettings() {
  const context = React.useContext(HostSettingsContext);
  if (!context) {
    throw new Error('useHostSettings deve ser usado dentro de um HostSettingsProvider');
  }
  return context;
}

export function useOptionalHostSettings() {
  return React.useContext(HostSettingsContext);
}
